import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { useEffect, useRef, useState } from 'react';

GlobalWorkerOptions.workerSrc = workerUrl;

/**
 * Draws the actual PDF bytes page by page. Browser PDF viewers differ (mobile Safari and some
 * embedded browsers show nothing), so the preview renders the file itself with pdf.js.
 */
export function PdfPages({ pdf }: { pdf: Uint8Array }) {
  const host = useRef<HTMLDivElement>(null);
  const [pages, setPages] = useState(0);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    let cancelled = false;
    // pdf.js takes ownership of the buffer it is given, so hand it a copy.
    const task = getDocument({ data: pdf.slice() });
    (async () => {
      const doc = await task.promise;
      if (cancelled) return;
      setPages(doc.numPages);
      // Measured for sharpness only; the page is shown at the width of its box by CSS, so a
      // box that has not settled yet cannot leave a tiny page behind.
      const width = Math.max(320, (el.parentElement?.clientWidth ?? el.clientWidth) - 24);
      const canvases: HTMLCanvasElement[] = [];
      for (let n = 1; n <= doc.numPages; n++) {
        const page = await doc.getPage(n);
        const base = page.getViewport({ scale: 1 });
        const scale = (width / base.width) * window.devicePixelRatio;
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = '100%';
        canvas.style.height = 'auto';
        canvas.className = 'mx-auto block bg-white shadow-sm ring-1 ring-black/10';
        canvas.setAttribute('aria-label', `Page ${n} of ${doc.numPages}`);
        await page.render({ canvas, viewport }).promise;
        if (cancelled) return;
        canvases.push(canvas);
      }
      el.replaceChildren(...canvases);
    })().catch(() => undefined);
    return () => {
      cancelled = true;
      void task.destroy();
    };
  }, [pdf]);

  return (
    <div className="min-h-0 flex-1 overflow-auto p-3">
      <div
        ref={host}
        className="grid gap-3"
        role="img"
        aria-label={`PDF preview, ${pages} page${pages === 1 ? '' : 's'}`}
      />
    </div>
  );
}
